import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ActionIconButton } from '@onesource/components';
import { FC, memo } from 'react';
import { IoIosWarning } from 'react-icons/io';
import { MdHistory, MdSave } from 'react-icons/md';

type Props = {
  isPending: boolean;
  saveDisabled: boolean;
  hasPendingRevision: boolean;
  onRevisionsClick: () => void;
  onSaveDraft: () => void;
};

/** Member-only footer buttons (Revisions, Save Draft) passed into the shared ProDirectoryStepFooter. */
const FooterActions: FC<Props> = ({ isPending, saveDisabled, hasPendingRevision, onRevisionsClick, onSaveDraft }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const buttonSize = isMobile ? 'medium' : 'large';

  return (
    <>
      {isMobile ? (
        <Badge color="warning" variant="dot" invisible={!hasPendingRevision}>
          <ActionIconButton
            icon={<MdHistory size={20} />}
            tooltip="Revisions"
            color="primary"
            onClick={onRevisionsClick}
          />
        </Badge>
      ) : (
        <Button
          type="button"
          size={buttonSize}
          variant="outlined"
          startIcon={<MdHistory />}
          sx={{ pr: 4 }}
          endIcon={
            hasPendingRevision ? (
              <Box component="span" sx={{ color: 'warning.main', display: 'flex' }}>
                <IoIosWarning size={16} />
              </Box>
            ) : undefined
          }
          onClick={onRevisionsClick}
        >
          Revisions
        </Button>
      )}

      {isMobile ? (
        <ActionIconButton
          icon={isPending ? <CircularProgress size={20} color="inherit" /> : <MdSave size={20} />}
          tooltip={isPending ? 'Saving...' : 'Save Draft'}
          color="primary"
          disabled={saveDisabled}
          onClick={onSaveDraft}
        />
      ) : (
        <Button
          type="button"
          size={buttonSize}
          variant="outlined"
          disabled={saveDisabled}
          startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : <MdSave />}
          onClick={onSaveDraft}
        >
          {isPending ? 'Saving...' : 'Save Draft'}
        </Button>
      )}
    </>
  );
};

export const ProDirectoryFooterActions = memo(FooterActions);
